import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { graphql } from 'gatsby';
import { get } from 'lodash';
import { Helmet } from 'react-helmet';
import slugify from 'slugify';
import Layout from '~components/Layout';
import Topper from '~components/base/Topper';
import BodyElement from '~components/body/BodyElement';
import ArchieElement from '~components/body/ArchieElement';
import SeoSocial from '~components/base/SeoSocial';
import useBusiness from '~lib/useBusiness';
import GlobalStyles from '../styles/GlobalStyles';
import Credits from '../components/base/Credits';
import Teasers from '../components/base/Teasers';
import Hole from '../components/custom/Hole';
import Scorecard from '../components/custom/Scorecard';
import IncrementalButtons from '../components/custom/IncrementalButtons';
import StickyBar from '../components/custom/StickyBar';
import AdPosition from '../components/base/AdPosition';
import Bylines from '../components/base/Bylines';

export const pageQuery = graphql`
    query {
        site {
            siteMetadata {
                title
                sectionName
                projectName
                baseUrl
            }
        }
        allGoogleSpreadsheetUsOpenSheet1 {
          edges {
            node {
              hole
              par
              yards
              holeOverview
              golfer
              fan
              didYouKnow
              # blurb2
              image
              rotatedImage {
                childImageSharp {
                 gatsbyImageData(width: 1600)
                }
              }
            }
          }
        }
        story: arcStory(storyId: {eq: "6B4PVMWNAFEATCXEIS5ULNIYRQ"}) {
            headlines {
                basic
            }
            subheadlines {
                basic
            }
            credits {
                name
                url
            }
            bodyElements {
              _id
              type
              subtype
              alignment
              content
              archieJson
              url
              srcSet
              caption
              subtype
              bodyElements {
                _id
                content
              }
              citation {
                content
              }
            }
            # getting seo social data
            promoImage {
              url
              srcSet
              caption
              credit {
                name
              }
            }
            description {
              basic
            }
            # getting teasers information
            relatedLinks {
              headlines {
                basic
              }
              promoImage {
                srcSet
                url
              }
              url
            }
            slug
        }
    }
`;

const IndexPage = ({
  data: {
    allGoogleSpreadsheetUsOpenSheet1,
    story,
    site: {
      pathPrefix,
      siteMetadata: { sectionName, projectName, baseUrl },
    },
  },
}) => {
  const slugifiedSection = slugify(sectionName, { lower: true });
  const authorNames = get(story, 'credits', [])
    .map((credit) => credit.name)
    .join(', ');

  const [currentHole, setCurrentHole] = useState(1);
  const [copied, markCopied] = useState(false);
  const HolesWrapperRef = useRef(null);

  useEffect(() => {
    const params = new Proxy(new URLSearchParams(window.location.search), {
      get: (searchParams, prop) => searchParams.get(prop),
    });

    const holeValue = parseInt(params.hole, 10);
    if (holeValue && !(Number.isNaN(holeValue))) {
      setCurrentHole(holeValue);
    }
  }, []);

  useBusiness({
    pageName: `${slugifiedSection} | ${get(story, 'headlines.basic')}`,
    environment: process.env.NODE_ENV,
    meterContent: (process.env.NODE_ENV !== 'development'),
    pageId: `apps.${story.slug}`,
    section: slugifiedSection,
    author: authorNames,
    projectName,
  });

  const subhed = get(story, 'subheadlines.basic')
    ? (<h3 className="subheadline miller-n4">{get(story, 'subheadlines.basic')}</h3>)
    : (null);
  const metaUrl = `https://apps.bostonglobe.com${pathPrefix}`;

  const firstGraph = story.bodyElements.findIndex((e) => e.type === 'text');
  story.bodyElements[firstGraph].dropCap = story.bodyElements[firstGraph].content[0];

  // testing for presence of publishDate archie element
  // if it exists, set the pubDateObj to it's value and pass to the Byline component below
  // if it doesn't pass null
  let pubDateObj = null;
  story.bodyElements.forEach((e) => {
    if (e.archieJson) {
      const parsedArchie = JSON.parse(e.archieJson);
      if (parsedArchie.publishDate !== undefined) {
        pubDateObj = parsedArchie;
      }
    }
  });

  const relatedLinks = get(story, 'relatedLinks');

  return (
    <Layout>
      <SeoSocial
        url={metaUrl}
        description={get(story, 'description.basic')}
        title={get(story, 'headlines.basic')}
        imageUrl={get(story, 'promoImage.sizes._1024')}
      />
      <Helmet
        link={[
          {
            rel: 'preconnet',
            href: 'https://fonts.googleapis.com',
          },
          {
            rel: 'preconnet',
            href: 'https://fonts.gstatic.com',
            crossOrigin: true,
          },
          {
            rel: 'stylesheet',
            href: 'https://fonts.googleapis.com/css2?family=Alfa+Slab+One&display=swap',
          },
        ]}
      />
      <GlobalStyles />
      <article>
        <Topper
          headline={get(story, 'headlines.basic')}
        >
          {subhed}
        </Topper>
        <Bylines data={story.credits} pubDate={pubDateObj} />
        {story.bodyElements.map((element, index) => {
          if (element.archieJson) {
            return (
              <ArchieElement
                data={element}
                key={element._id}
                relatedLinks={get(story, 'relatedLinks')}
              />
            );
          }
          return (
            <BodyElement
              data={element}
              key={`${element._id}-${index}`}
            />
          );
        })}
        <AdPosition positionId="ad_inarticle1" overline />
        <div>
          <Scorecard
            data={allGoogleSpreadsheetUsOpenSheet1}
            currentHole={currentHole}
            setCurrentHole={setCurrentHole}
          />
          <div className="holes__wrapper" ref={HolesWrapperRef}>
            {allGoogleSpreadsheetUsOpenSheet1.edges.map((row) => (
              <Hole
                data={row.node}
                currentHole={currentHole}
                key={`hole-${row.node.hole}`}
                copied={copied}
                markCopied={markCopied}
                url={baseUrl}
              />
            ))}
          </div>
          <IncrementalButtons
            currentHole={currentHole}
            setCurrentHole={setCurrentHole}
            HolesWrapperRef={HolesWrapperRef}
          />
        </div>
        <AdPosition positionId="ad_inarticle2" overline />
        {relatedLinks.length > 0 && <Teasers relatedlinks={relatedLinks} />}
        <Credits />
      </article>
    </Layout>
  );
};

IndexPage.propTypes = {
  data: PropTypes.shape({
    story: PropTypes.shape({
      bodyElements: PropTypes.arrayOf(
        PropTypes.shape({
          archieJson: PropTypes.string,
          dropCap: PropTypes.string,
          content: PropTypes.string,
        }),
      ),
      credits: PropTypes.arrayOf(
        PropTypes.shape(),
      ),
      slug: PropTypes.string,
    }),
    allGoogleSpreadsheetUsOpenSheet1: PropTypes.shape(),
    site: PropTypes.shape({
      pathPrefix: PropTypes.string.isRequired,
      siteMetadata: PropTypes.shape({
        title: PropTypes.string.isRequired,
        sectionName: PropTypes.string.isRequired,
        projectName: PropTypes.string.isRequired,
        baseUrl: PropTypes.string.isRequired,
      }).isRequired,
    }).isRequired,
  }).isRequired,
  pageContext: PropTypes.shape({
    storyId: PropTypes.string,
  }).isRequired,
};

export default IndexPage;
